import { action, computed, makeObservable, observable } from 'mobx';
import { ISelectOption } from 'telia-front-react';

import ApiService from '../services/ApiService';
import { parseInvoiceType, trim } from '../util/FormatUtil';

import EInvoiceSuppliersDataStore from './data/EInvoiceSuppliersDataStore';
import InvoiceNameStore from './InvoiceNameStore';
import { Address } from './models/Address';
import { AddressValidationResult } from './models/AddressValidation';
import { FileTypeOption, InvoiceDeliveryType, InvoiceSettings } from './models/CustomerInvoicesSettings';
import { BankAddressType, EInvoiceSupplier, InvoiceCentralAddressType } from './models/EInvoiceSupplier';
import { EInvoiceSupplierOption } from './models/EInvoiceSupplierOption';
import { IbanValidation, IbanValidationResult } from './models/IbanValidation';
import { InvoiceType } from './models/InvoiceType';
import { MobileNumberValidation, MobileNumberValidationResult } from './models/MobileNumberValidation';
import { ISsoStore } from './SsoStore';
import InvoiceSettingsPanelEmailValidationViewStore from './view/InvoiceSettingsPanelEmailValidationViewStore';

type InvoiceLanguage = 'et' | 'ru' | 'en';
type SettingsStatus = 'loading' | 'loaded' | 'not-found' | 'failed';

export interface IEmailAddressFieldState {
  value: string;
  hasError: boolean;
}

export interface IMobileNumberFieldState {
  value: string;
  hasError: boolean;
}

export default class InvoiceSettingsPanelStore {
  @observable
  public isSaveFailed = false;
  @observable
  public ruleViolation = false;
  @observable
  public status: SettingsStatus = 'loading';
  @observable
  public invoiceSettings: InvoiceSettings = null;
  @observable
  public originalSettings: InvoiceSettings = null;
  @observable
  public bankAddressType: BankAddressType = null;
  @observable
  public invoiceCentralAddressType: InvoiceCentralAddressType = null;
  @observable
  public mobileNumberValidationResult: MobileNumberValidationResult = 'VALID';
  @observable
  public addressValidationResult: AddressValidationResult = 'VALID';
  @observable
  private shouldValidate = false;
  @observable
  private ibanValidationResult: IbanValidationResult = 'VALID';

  constructor(
    private apiService: ApiService,
    private invoiceNameStore: InvoiceNameStore,
    private ssoStore: ISsoStore,
    private eInvoiceSuppliersDataStore: EInvoiceSuppliersDataStore,
    private invoiceSettingsPanelEmailValidationViewStore: InvoiceSettingsPanelEmailValidationViewStore
  ) {
    makeObservable(this);
  }

  @action
  public initData(): void {
    this.isSaveFailed = false;
    this.shouldValidate = false;
    this.invoiceSettings = null;
    this.originalSettings = null;
    this.status = 'loading';
  }

  @action
  public setSettings(settings: InvoiceSettings): void {
    if (settings == null) {
      this.status = 'not-found';
      return;
    }
    this.invoiceSettings = settings;
    this.originalSettings = structuredClone(settings);
    if (!this.multipleDeliveryOptionsSupported) {
      if (!this.invoiceSettings.bankRecipientCode || !this.invoiceSettings.bankRecipientName) {
        this.invoiceSettings.bankRecipientCode = this.ssoStore.customer.registrationNumber;
        this.invoiceSettings.bankRecipientName = this.ssoStore.customer.displayName;
        this.bankAddressType = 'IBAN';
      }
    } else {
      if (!this.invoiceSettings.invoiceDeliveries.includes('BANK')) {
        this.invoiceSettings.bankRecipientCode = null;
      }

      if (this.eInvoiceSuppliersDataStore.eInvoiceSuppliers.isFinishedWithError) {
        this.status = 'failed';
        return;
      } else {
        this.bankAddressType = this.findBankAddressType(this.invoiceSettings.bankCode);
        this.invoiceCentralAddressType = this.findInvoiceCentralAddressType(this.invoiceSettings.invoiceCentralCode);
      }
    }
    this.status = 'loaded';
  }

  public get invoiceCentralSupported(): boolean {
    return this.ssoStore.isBusinessCustomer && (this.invoiceType === InvoiceType.MOBILE || this.invoiceType === InvoiceType.BUSINESS);
  }

  public get multipleDeliveryOptionsSupported(): boolean {
    return this.invoiceSettings.source === 'BILL';
  }

  @computed
  public get originalTitle(): string {
    return this.invoiceNameStore.refNumberName(this.originalSettings.contentType, this.originalSettings.title, this.originalSettings.source);
  }

  @computed
  public get invoiceLanguages(): InvoiceLanguage[] {
    const languages: InvoiceLanguage[] = ['et'];

    if (this.invoiceSettings.source === 'ARGOS' || this.invoiceSettings.source === 'BILL') {
      languages.push('ru', 'en');
    }
    return languages;
  }

  @action
  public addEmail(): void {
    this.invoiceSettings.emailAddresses.push('');
  }

  @action
  public setEmail(index: number, value: string): void {
    this.invoiceSettingsPanelEmailValidationViewStore.setEmailsValidation(index, true);
    this.invoiceSettings.emailAddresses[index] = value;
  }

  @action
  public updateMobile(value: string): void {
    this.setMobileNumberValidation('VALID');
    this.invoiceSettings.mobileNumber = value;
  }

  @action
  public updateInvoiceSettings(value: Partial<InvoiceSettings>): void {
    Object.assign(this.invoiceSettings, value);
  }

  @action
  public updateIban(iban: string): void {
    this.ibanValidationResult = null;
    this.invoiceSettings.ibanOrUserCode = iban;
  }

  @action
  public updateInvoiceCentralAddress(invoiceCentralAddress: string): void {
    this.invoiceSettings.invoiceCentralAddress = invoiceCentralAddress;
  }

  @action
  public updateTitle(title: string): void {
    this.invoiceSettings.title = title;
  }

  @action
  public updateLanguage(lang: string): void {
    this.invoiceSettings.invoiceLanguage = lang;
  }

  @action
  public addInvoiceDelivery(invoiceDelivery: InvoiceDeliveryType): void {
    if (invoiceDelivery === 'EMAIL' && this.invoiceSettings.emailAddresses.length === 0) {
      this.addEmail();
    }
    if (this.multipleDeliveryOptionsSupported) {
      this.invoiceSettings.invoiceDeliveries.push(invoiceDelivery);
    } else {
      this.invoiceSettings.invoiceDeliveries = [invoiceDelivery];
      if (invoiceDelivery === 'INVOICE_CENTRAL') {
        this.setInvoiceCentralSupplier('OMNIVA');
      }
    }
    if (this.showCancelPostDelivery(invoiceDelivery)) {
      this.removeInvoiceDelivery('POST');
    }
  }

  @action
  public removeInvoiceDelivery(invoiceDelivery: InvoiceDeliveryType): void {
    this.invoiceSettings.invoiceDeliveries = this.invoiceSettings.invoiceDeliveries.filter((delivery) => delivery !== invoiceDelivery);
  }

  @action
  public clearInvoiceDeliveries(): void {
    this.invoiceSettings.invoiceDeliveries = [];
  }

  @action
  public selectAddress(value: Address, roomName: string): void {
    this.invoiceSettings.address = { ...value, roomName };
    this.setAddressValidation('VALID');
  }

  @action
  public async validateInvoiceSettings(): Promise<any> {
    try {
      this.setShouldValidate(true);

      const ibanValidationPromise = this.validateIbanIfNeeded();
      const emailValidationPromise = this.validateEmailsIfNeeded();
      const mobileNumberValidationPromise = this.validateMobileNumberIfNeeded();

      await ibanValidationPromise;
      await emailValidationPromise;
      await mobileNumberValidationPromise;
    } catch (e) {
      this.setShouldValidate(true);
      throw e;
    }
  }

  public trimInvoiceSettings(): void {
    this.trimAndReplaceInvoiceSettingsValues();
    this.removeEmptyEmails();
  }

  @action
  public removeEmptyEmails(): void {
    if (this.invoiceSettings.emailAddresses.length === 2 && !this.invoiceSettings.emailAddresses[1]) {
      this.invoiceSettings.emailAddresses = [this.invoiceSettings.emailAddresses[0]];
    }
  }

  @action
  public setFileType(fileType: FileTypeOption): void {
    const fileTypes = this.invoiceSettings.fileTypes;
    if (!fileTypes.includes(fileType)) {
      fileTypes.push(fileType);
    } else {
      fileTypes.splice(fileTypes.indexOf(fileType), 1);
    }
  }

  @action
  public setBankNameOption(bankOption: ISelectOption): void {
    this.bankAddressType = this.eInvoiceSuppliersDataStore.banks.find((bank) => bank.name === bankOption.label).channelAddressType;
    this.invoiceSettings.bankCode = bankOption.value;
    if (this.bankAddressType === 'REG_NUM') {
      this.invoiceSettings.ibanOrUserCode = this.ssoStore.customer.registrationNumber;
    } else {
      this.invoiceSettings.ibanOrUserCode = '';
    }
    this.ibanValidationResult = null;
  }

  public get bankNameOptions(): EInvoiceSupplierOption[] {
    const bankNameOptions: EInvoiceSupplierOption[] = [];

    if (this.multipleDeliveryOptionsSupported) {
      this.eInvoiceSuppliersDataStore.banks.forEach((bank, index) => {
        bankNameOptions.push({
          id: `invoice-settings-panel-bank-name-dropdown-selection-${index}`,
          value: bank.channelCode,
          label: bank.name
        });
      });
    }

    return bankNameOptions;
  }

  public get invoiceCentralOptions(): EInvoiceSupplierOption[] {
    const invoiceCentralOptions: EInvoiceSupplierOption[] = [];

    if (this.multipleDeliveryOptionsSupported) {
      this.eInvoiceSuppliersDataStore.invoiceCentrals.forEach((invoiceCentral: EInvoiceSupplier, index) => {
        invoiceCentralOptions.push({
          id: `invoice-settings-panel-invoice-central-dropdown-selection-${index}`,
          value: invoiceCentral.channelCode,
          label: invoiceCentral.name
        });
      });
    }

    return invoiceCentralOptions;
  }

  @action
  public setInvoiceCentralOption(invoiceCentralOption: ISelectOption): void {
    this.invoiceCentralAddressType = this.eInvoiceSuppliersDataStore.invoiceCentrals.find(
      (invoiceCentral) => invoiceCentral.name === invoiceCentralOption.label
    ).channelAddressType;
    this.invoiceSettings.invoiceCentralCode = invoiceCentralOption.value;
    this.invoiceSettings.invoiceCentralSupplier = this.eInvoiceSuppliersDataStore.invoiceCentrals.find(
      (invoiceCentral) => invoiceCentral.channelCode === invoiceCentralOption.value
    ).name;
    if (this.invoiceCentralAddressType === 'REG_NUM') {
      this.invoiceSettings.invoiceCentralAddress = this.ssoStore.customer.registrationNumber;
    } else {
      this.invoiceSettings.invoiceCentralAddress = '';
    }
  }

  @computed
  public get invoiceFileTypes(): FileTypeOption[] {
    const source = this.invoiceSettings.source;
    const fileTypes: FileTypeOption[] = ['PDF'];

    if (source !== 'TBCIS') {
      fileTypes.push('XML');
    }
    return fileTypes;
  }

  @computed
  public get fileTypesHasError(): boolean {
    return this.shouldValidate && this.invoiceSettings.invoiceDeliveries.includes('EMAIL') && this.invoiceSettings.fileTypes.length === 0;
  }

  @computed
  public get icon(): string {
    return this.invoiceNameStore.invoiceIconName(this.originalSettings.contentType);
  }

  @computed
  public get title(): string {
    return this.invoiceNameStore.refNumberName(this.originalSettings.contentType, this.invoiceSettings.title, this.invoiceSettings.source);
  }

  @computed
  public get editableBillTitle(): boolean {
    return this.invoiceSettings.source !== 'TBCIS';
  }

  @computed
  public get hasValidationError(): boolean {
    return (
      this.addressHasError ||
      this.addressRoomIsNotValid ||
      this.addressRoomIsMissing ||
      this.emailAddressesHasError ||
      this.bankUserNameHasError ||
      this.bankUserCodeHasError ||
      this.bankOptionAccountNumberUnsupported ||
      this.bankOptionAccountNumberInvalid ||
      this.bankOptionAccountNumberEInvoiceNotSupported ||
      this.bankOptionAccountNumberAndBankCodeMismatch ||
      this.bankOptionIbanInvalid ||
      this.eInvoiceSuppliersFailedToLoad ||
      this.fileTypesHasError ||
      this.invoiceCentralOptionAddressInvalid ||
      this.mobileOptionNumberInvalid
    );
  }

  @computed
  public get addressRoomIsNotValid(): boolean {
    return this.shouldValidateAddress && this.addressValidationResult === 'NOT_VALID';
  }

  @computed
  public get addressRoomIsMissing(): boolean {
    return this.shouldValidateAddress && this.addressValidationResult === 'MISSING';
  }

  @computed
  public get emailAddressesHasError(): boolean {
    return !!this.emailAddresses.find((emailAddress) => emailAddress.hasError);
  }

  @computed
  public get bankOptionAccountNumberUnsupported(): boolean {
    return this.shouldValidateIban && this.ibanValidationResult === 'UNSUPPORTED_IBAN';
  }

  @computed
  public get bankOptionAccountNumberInvalid(): boolean {
    return this.shouldValidateIban && (!this.invoiceSettings.ibanOrUserCode || this.ibanValidationResult === 'INVALID');
  }

  @computed
  public get bankOptionAccountNumberError(): boolean {
    return (
      this.bankOptionAccountNumberAndBankCodeMismatch ||
      this.bankOptionAccountNumberInvalid ||
      this.bankOptionAccountNumberEInvoiceNotSupported ||
      this.bankOptionAccountNumberUnsupported
    );
  }

  @computed
  public get eInvoiceSuppliersFailedToLoad(): boolean {
    const { eInvoiceSuppliers } = this.eInvoiceSuppliersDataStore;

    return this.multipleDeliveryOptionsSupported && eInvoiceSuppliers.isFinishedWithError;
  }

  @computed
  public get mobileOptionNumberInvalid(): boolean {
    return this.shouldValidateMobileNumber && this.mobileNumberValidationResult !== 'VALID';
  }

  @computed
  public get bankOptionIbanInvalid(): boolean {
    return this.shouldValidateIban && this.bankAddressType === 'IBAN' && (!this.invoiceSettings.ibanOrUserCode || this.ibanValidationResult === 'INVALID');
  }

  @computed
  public get invoiceCentralOptionAddressInvalid(): boolean {
    if (
      this.shouldValidate &&
      this.multipleDeliveryOptionsSupported &&
      this.ssoStore.isBusinessCustomer &&
      this.invoiceSettings.invoiceDeliveries.includes('INVOICE_CENTRAL')
    ) {
      return !this.invoiceSettings.invoiceCentralAddress;
    }
    return false;
  }

  @computed
  public get bankOptionAccountNumberEInvoiceNotSupported(): boolean {
    return this.shouldValidateIban && this.ibanValidationResult === 'EINVOICE_NOT_SUPPORTED';
  }

  @computed
  public get bankOptionAccountNumberAndBankCodeMismatch(): boolean {
    return this.shouldValidateIban && this.ibanValidationResult === 'BANK_CODE_IBAN_MISMATCH';
  }

  @computed
  private get shouldValidateIban(): boolean {
    return this.shouldValidate && this.invoiceSettings.invoiceDeliveries.includes('BANK');
  }

  @computed
  private get shouldValidateEmail(): boolean {
    return this.shouldValidate && this.invoiceSettings.invoiceDeliveries.includes('EMAIL') && !!this.invoiceSettings.emailAddresses[0];
  }

  @computed
  private get shouldValidateAddress(): boolean {
    return this.shouldValidate && (!this.invoiceSettings.isRecipientSameAsCustomer || this.invoiceSettings.invoiceDeliveries.includes('POST'));
  }

  @computed
  private get shouldValidateMobileNumber(): boolean {
    return this.shouldValidate && this.invoiceSettings.invoiceDeliveries.includes('MOBILE');
  }

  @computed
  public get bankUserCodeHasError(): boolean {
    if (!this.multipleDeliveryOptionsSupported) {
      return this.shouldValidate && this.invoiceSettings.invoiceDeliveries.includes('BANK') && !this.invoiceSettings.bankRecipientCode;
    }
    return false;
  }

  @computed
  public get addEmailAllowed(): boolean {
    return this.invoiceSettings.source !== 'TBCIS' && this.emailAddresses.length < 2;
  }

  @computed
  public get bankUserNameHasError(): boolean {
    if (this.invoiceSettings.isRecipientSameAsCustomer === true) {
      return false;
    }
    return (
      this.shouldValidate &&
      (this.invoiceSettings.invoiceDeliveries.includes('BANK') || this.invoiceSettings.isRecipientSameAsCustomer === false) &&
      !this.invoiceSettings.bankRecipientName
    );
  }

  @computed
  public get emailAddresses(): IEmailAddressFieldState[] {
    return this.invoiceSettings.emailAddresses.map((emailAddress, index) => {
      const shouldValidateEmail = this.shouldValidate && (index === 0 || emailAddress) && this.invoiceSettings.invoiceDeliveries.includes('EMAIL');
      return {
        value: emailAddress,
        hasError: shouldValidateEmail && ((index === 0 && !emailAddress) || !this.invoiceSettingsPanelEmailValidationViewStore.isValidEmail(index))
      };
    });
  }

  @computed
  public get mobileNumber(): IMobileNumberFieldState {
    const mobileNumber = this.invoiceSettings.mobileNumber;
    return {
      value: mobileNumber,
      hasError: this.mobileOptionNumberInvalid
    };
  }

  @computed
  public get apartmentHasError(): boolean {
    if (this.invoiceSettings.isRecipientSameAsCustomer && !this.invoiceSettings.invoiceDeliveries.includes('POST')) {
      return false;
    }
    this.validateAddressIfNeeded();
    return this.addressValidationResult === 'NOT_VALID' || this.addressValidationResult === 'MISSING';
  }

  @computed
  public get addressHasError(): boolean {
    if (this.invoiceSettings.isRecipientSameAsCustomer === true && !this.invoiceSettings.invoiceDeliveries.includes('POST')) {
      return false;
    }
    return (
      this.shouldValidate &&
      (this.invoiceSettings.invoiceDeliveries.includes('POST') || this.invoiceSettings.isRecipientSameAsCustomer === false) &&
      (!this.invoiceSettings.address || !this.invoiceSettings.address.fullAddressWithoutRoomName)
    );
  }

  @computed
  public get invoiceSource(): string {
    return this.invoiceSettings ? this.invoiceSettings.source : null;
  }

  @computed
  private get invoiceType(): InvoiceType {
    return parseInvoiceType(this.invoiceSettings.contentType, this.ssoStore.customer.type);
  }

  @action
  public markSaveFailed(): void {
    this.isSaveFailed = true;
  }

  @action
  public setInvoiceCentralSupplier(supplier: string): void {
    this.invoiceSettings.invoiceCentralSupplier = supplier;
  }

  @action
  public setRuleViolation(): void {
    this.ruleViolation = true;
  }

  @action
  public setRecipientSameAsCustomer(sameAsCustomer: boolean): void {
    if (sameAsCustomer === false) {
      this.invoiceSettings.bankRecipientName = null;
      this.invoiceSettings.address = null;
    }
    this.invoiceSettings.isRecipientSameAsCustomer = sameAsCustomer;
  }

  public findBankAddressType(bankCode: string): BankAddressType {
    if (bankCode != null) {
      return this.eInvoiceSuppliersDataStore.banks.find((bank) => bank.channelCode === bankCode).channelAddressType;
    }
    return null;
  }

  public validateAddressIfNeeded(): void {
    if (!this.shouldValidateAddress) {
      return;
    }
    if (this.invoiceSettings.address?.rooms != null && this.invoiceSettings.address.rooms.length > 0) {
      if (this.invoiceSettings.address.roomName.length < 1) {
        this.setAddressValidation('MISSING');
      } else if (!this.invoiceSettings.address.rooms.includes(this.invoiceSettings.address.roomName)) {
        const findMatches = this.invoiceSettings.address.rooms.filter((a) =>
          a.toLowerCase().match(/(\d+)/).includes(this.invoiceSettings.address.roomName.toLowerCase())
        );
        findMatches.length === 1 ? (this.invoiceSettings.address.roomName = findMatches[0]) : this.setAddressValidation('NOT_VALID');
      }
    }
  }

  public async validateIbanIfNeeded(): Promise<any> {
    const iban = this.invoiceSettings.ibanOrUserCode;
    if (this.bankAddressType !== 'IBAN') {
      this.setIbanValidation({ ibanValidationResult: 'VALID' });
      return;
    }

    if (this.shouldValidateIban && iban) {
      const ibanValidation: IbanValidation = await this.apiService.validateAccount(iban, this.invoiceSettings.bankCode);

      this.setIbanValidation(ibanValidation);
    }
  }

  public async validateMobileNumberIfNeeded(): Promise<any> {
    if (!this.invoiceSettings.mobileNumber) {
      this.setMobileNumberValidation('NOT_A_NUMBER');
    } else if (this.shouldValidateMobileNumber) {
      const mobileNumberValidation: MobileNumberValidation = await this.apiService.validateMobileNumber(this.invoiceSettings.mobileNumber);
      this.setMobileNumberValidation(mobileNumberValidation.validate);
    }
  }

  public async validateEmailsIfNeeded(): Promise<any> {
    if (this.shouldValidateEmail) {
      await this.invoiceSettingsPanelEmailValidationViewStore.loadEmailsValidation(this.invoiceSettings.emailAddresses);
    }
  }

  @action
  private setIbanValidation(ibanValidation: IbanValidation) {
    this.ibanValidationResult = ibanValidation.ibanValidationResult;
  }

  @action
  private setMobileNumberValidation(mobileNumberValidationResult: MobileNumberValidationResult) {
    this.mobileNumberValidationResult = mobileNumberValidationResult;
  }

  @action
  private setAddressValidation(addressValidationResult: AddressValidationResult) {
    this.addressValidationResult = addressValidationResult;
  }

  @action
  private setShouldValidate(shouldValidate: boolean) {
    this.shouldValidate = shouldValidate;
  }

  private findInvoiceCentralAddressType(invoiceCentralCode: string) {
    if (invoiceCentralCode != null) {
      return this.eInvoiceSuppliersDataStore.invoiceCentrals.find((invoiceCentral) => invoiceCentral.channelCode === invoiceCentralCode).channelAddressType;
    }
    return null;
  }

  @action
  public trimAndReplaceInvoiceSettingsValues(): void {
    this.invoiceSettings = {
      ...this.invoiceSettings,
      emailAddresses: this.invoiceSettings.emailAddresses && this.invoiceSettings.emailAddresses.map((emailAddress) => trim(emailAddress)),
      title: trim(this.invoiceSettings.title),
      bankRecipientName: trim(this.invoiceSettings.bankRecipientName),
      bankRecipientCode: trim(this.invoiceSettings.bankRecipientCode),
      mobileNumber: this.invoiceSettings.mobileNumber && this.invoiceSettings.mobileNumber.replace(/ /g, '').replace(/\(/g, '').replace(/\)/g, ''),
      address: this.invoiceSettings.address && {
        ...this.invoiceSettings.address,
        roomName: trim(this.invoiceSettings.address.roomName)
      }
    };
  }

  public get isPostDeliveryCancelled(): boolean {
    return !this.invoiceSettings.invoiceDeliveries.includes('POST');
  }

  @action
  public togglePostDeliveryCancelOption(isCancelled: boolean): void {
    isCancelled ? this.removeInvoiceDelivery('POST') : this.addInvoiceDelivery('POST');
  }

  public showCancelPostDelivery(deliveryType: InvoiceDeliveryType): boolean {
    if (!this.multipleDeliveryOptionsSupported) {
      return false;
    }
    if (deliveryType === 'POST') {
      return false;
    }
    if (!this.originalSettings.invoiceDeliveries.includes('POST')) {
      return false;
    }
    return !this.originalSettings.invoiceDeliveries.includes(deliveryType);
  }
}
